import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DEFAULT_TABLE_PAGE_SIZE } from '../../utils/tableVariables';
import { WorkflowProgressValue } from '../../utils/workflow';

type TTRState = {
  // Pagination
  page: number;
  pageSize: number;

  // Search/Filter
  search: string;
  workflowFilter: WorkflowProgressValue | 'all';

  // Delete Dialog
  isDeleteDialogOpen: boolean;
  trToDelete: { id: string; trNumber: string } | null;

  // Snackbar notifications
  snackbar: {
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  };
};

const initialState: TTRState = {
  page: 0,
  pageSize: DEFAULT_TABLE_PAGE_SIZE,
  search: '',
  workflowFilter: 'all',
  isDeleteDialogOpen: false,
  trToDelete: null,
  snackbar: {
    open: false,
    message: '',
    severity: 'success',
  },
};

const trSlice = createSlice({
  name: 'tr',
  initialState,
  reducers: {
    // Pagination
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
    },

    // Search - reset page when search changes
    setSearch: (state, action: PayloadAction<string>) => {
      state.search = action.payload;
      state.page = 0;
    },

    // Workflow filter - reset page when filter changes
    setWorkflowFilter: (state, action: PayloadAction<WorkflowProgressValue | 'all'>) => {
      state.workflowFilter = action.payload;
      state.page = 0;
    },

    // Delete dialog
    openDeleteDialog: (state, action: PayloadAction<{ id: string; trNumber: string }>) => {
      state.isDeleteDialogOpen = true;
      state.trToDelete = action.payload;
    },
    closeDeleteDialog: (state) => {
      state.isDeleteDialogOpen = false;
      state.trToDelete = null;
    },

    // Snackbar
    showSnackbar: (state, action: PayloadAction<{ message: string; severity: 'success' | 'error' | 'warning' | 'info' }>) => {
      state.snackbar = {
        open: true,
        message: action.payload.message,
        severity: action.payload.severity,
      };
    },
    hideSnackbar: (state) => {
      state.snackbar.open = false;
    },

    // Reset all state
    resetTRState: () => initialState,
  },
});

export const {
  setPage,
  setPageSize,
  setSearch,
  setWorkflowFilter,
  openDeleteDialog,
  closeDeleteDialog,
  showSnackbar,
  hideSnackbar,
  resetTRState,
} = trSlice.actions;

export default trSlice.reducer;
